import axios from 'axios'
import {API_URL} from '../../../../../../_metronic/helpers'
import {
  PrintImageKind,
  PrintTemplate,
  PrintTemplateFieldError,
  PrintTemplateInput,
  PrintTemplateList,
  PrintTemplatePreview,
  StoredPrintImage,
} from './_models'

// MegaPayBackend PrintTemplatesController. Refusals are {code, message, errors} and never 401, so a
// refusal never signs the user out.

export class PrintStudioError extends Error {
  readonly code: string
  readonly status: number | null
  readonly errors: PrintTemplateFieldError[]
  /** The body of the refusal, for a value-added service's fee (BUSINESS_SERVICE_CHARGE_CONFIRM: amount, items). */
  readonly data?: unknown

  constructor(
    code: string,
    message: string,
    status: number | null,
    errors: PrintTemplateFieldError[] = [],
    data?: unknown
  ) {
    super(message)
    this.code = code
    this.status = status
    this.errors = errors
    this.data = data
  }
}

const FALLBACK_MESSAGES: Record<string, string> = {
  network: 'اتصال به سامانه برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.',
  server: 'درخواست انجام نشد. چند لحظه دیگر دوباره تلاش کنید.',
}

export const toPrintStudioError = (error: unknown): PrintStudioError => {
  if (error instanceof PrintStudioError) return error
  if (!axios.isAxiosError(error) || !error.response) {
    return new PrintStudioError('NETWORK', FALLBACK_MESSAGES.network, null)
  }
  const data = error.response.data as
    | {code?: unknown; message?: unknown; errors?: unknown}
    | undefined
  const errors = Array.isArray(data?.errors)
    ? (data!.errors as PrintTemplateFieldError[]).filter(
        (item) => item && typeof item.field === 'string' && typeof item.message === 'string'
      )
    : []
  return new PrintStudioError(
    typeof data?.code === 'string' ? data.code : 'SERVER',
    typeof data?.message === 'string' ? data.message : FALLBACK_MESSAGES.server,
    error.response.status,
    errors,
    data
  )
}

const call = async <T>(request: () => Promise<{data: T}>): Promise<T> => {
  try {
    return (await request()).data
  } catch (error) {
    throw toPrintStudioError(error)
  }
}

export const listPrintTemplates = (companyId: number, signal?: AbortSignal) =>
  call(() =>
    axios.get<PrintTemplateList>(`${API_URL}/print-templates`, {params: {companyId}, signal})
  )

export const getPrintTemplate = (companyId: number, id: number, signal?: AbortSignal) =>
  call(() =>
    axios.get<PrintTemplate>(`${API_URL}/print-templates/${id}`, {params: {companyId}, signal})
  )

export const savePrintTemplate = (input: PrintTemplateInput) =>
  call(() =>
    input.id > 0
      ? axios.put<PrintTemplate>(`${API_URL}/print-templates/${input.id}`, input)
      : axios.post<PrintTemplate>(`${API_URL}/print-templates`, input)
  )

export const setDefaultPrintTemplate = (companyId: number, id: number) =>
  call(() => axios.post<PrintTemplateList>(`${API_URL}/print-templates/${id}/default`, {companyId}))

export const previewPrintTemplate = (input: PrintTemplateInput, signal?: AbortSignal) =>
  call(() =>
    axios.post<PrintTemplatePreview>(`${API_URL}/print-templates/preview`, input, {signal})
  )

/** Stores an edited picture (or its original) for a template of this business. */
export const uploadPrintTemplateImage = (
  companyId: number,
  kind: PrintImageKind,
  blob: Blob,
  original: boolean
) => {
  const form = new FormData()
  const extension = blob.type === 'image/jpeg' ? 'jpg' : 'png'
  form.append('file', blob, `${kind}${original ? '-original' : ''}.${extension}`)
  return call(() =>
    axios.post<StoredPrintImage>(`${API_URL}/print-templates/images`, form, {
      params: {companyId, kind, original},
    })
  )
}
